import styled, { css } from 'styled-components'
import { Tokens } from '@/assets/tokens'

export const ComponentContainer = styled.div<{ $fit: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $fit }) =>
    !$fit &&
    css`
      max-width: 20rem;
    `}
`

export const Label = styled.span`
  font-size: 14px;
  color: ${Tokens.ColorTextPrimary};
`

export const SelectionContainer = styled.div<{ $inverted: boolean; $disabled?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  height: ${Tokens.HeightInput};
  padding: 0 16px;
  align-items: center;
  gap: 8px;
  border-radius: ${Tokens.RadiusInput};
  background-color: ${({ $inverted }) => ($inverted ? Tokens.ColorUIPrimary : Tokens.ColorBackground)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? '0.5' : '1')};

  &:hover {
    background-color: ${({ $inverted, $disabled }) => {
      if ($disabled) return $inverted ? Tokens.ColorUIPrimary : Tokens.ColorBackground
      return $inverted ? Tokens.ColorUIPrimaryDim : Tokens.ColorHover
    }};
  }

  & > img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`

export const SelectedItem = styled.div<{ $placeholderColor: string; $hasValue: boolean }>`
  flex: 1;
  color: ${({ $placeholderColor, $hasValue }) => ($hasValue ? Tokens.ColorTextPrimary : $placeholderColor)};
  overflow: hidden;

  ${({ $hasValue }) =>
    $hasValue &&
    css`
      color: ${Tokens.ColorTextPrimary} !important;
    `}

  ${({ $hasValue, $placeholderColor }) =>
    !$hasValue &&
    css`
      color: ${$placeholderColor} !important;
    `}
`

export const SelectedItemsText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  color: inherit;
`

export const Selector = styled.ul<{ $show?: boolean }>`
  all: unset;

  position: absolute;
  top: ${Tokens.HeightInput};
  left: 0;

  border: ${Tokens.ColorWhite} solid 0.3rem;
  border-radius: 1rem;
  width: auto;
  min-width: 200px;
  height: fit-content;
  max-height: 300px;
  padding: 0.5rem;
  overflow-y: auto;

  display: none;

  font-size: 14px;
  font-weight: 400;
  background-color: ${Tokens.ColorBackground};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  cursor: pointer;

  z-index: 9999;
  transition: display ease-in-out 0.25s;

  ${({ $show: show }) =>
    show &&
    css`
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: stretch;
      gap: 0.2rem;
    `}
`

export const SelectorOptions = styled.li<{ $selected: boolean }>`
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  background: ${({ $selected }) => ($selected ? Tokens.ColorBackgroundTertiary : 'transparent')};
  color: ${Tokens.ColorTextPrimary};

  &:hover {
    color: ${Tokens.ColorWhite};
    background-color: ${Tokens.ColorUIPrimary};
  }
`

export const Checkbox = styled.div<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  min-width: 16px;
  flex-shrink: 0;
  border: 2px solid ${Tokens.ColorUIPrimary};
  border-radius: 4px;
  background: ${({ $selected }) => ($selected ? Tokens.ColorUIPrimary : 'transparent')};
  position: relative;
  box-sizing: border-box;
  cursor: pointer;

  ${SelectorOptions}:hover & {
    border-color: ${Tokens.ColorWhite};
    background: ${({ $selected }) => ($selected ? Tokens.ColorWhite : 'transparent')};
  }

  &:after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};
    color: ${({ $selected }) => ($selected ? Tokens.ColorWhite : 'transparent')};
    font-size: 12px;
    line-height: 1;
    font-weight: bold;
  }

  ${SelectorOptions}:hover &:after {
    color: ${Tokens.ColorUIPrimary};
  }
`

export const ExclusionCheckbox = styled.div<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  min-width: 16px;
  flex-shrink: 0;
  border: 2px solid ${Tokens.ColorUIDanger};
  border-radius: 4px;
  background: ${({ $selected }) => ($selected ? Tokens.ColorUIDanger : 'transparent')};
  position: relative;
  box-sizing: border-box;
  cursor: pointer;

  ${SelectorOptions}:hover & {
    border-color: ${Tokens.ColorWhite};
    background: ${({ $selected }) => ($selected ? Tokens.ColorWhite : 'transparent')};
  }

  &:after {
    content: '✗';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};
    color: ${({ $selected }) => ($selected ? Tokens.ColorWhite : 'transparent')};
    font-size: 12px;
    line-height: 1;
    font-weight: bold;
  }

  ${SelectorOptions}:hover &:after {
    color: ${Tokens.ColorUIDanger};
  }
`

export const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background-color: ${Tokens.ColorBackgroundSecondary};
  border-bottom: 1px solid ${Tokens.ColorBorder};
`

export const SearchInput = styled.input`
  all: unset;
  flex: 1;
  font-size: 14px;
  color: ${Tokens.ColorTextPrimary};

  &::placeholder {
    color: ${Tokens.ColorTextTertiary};
  }
`
